import { cloneDeep } from 'lodash'
import { Dispatch, SetStateAction, MouseEvent as ReactMouseEvent, MutableRefObject, useCallback } from 'react'
import { dragExceededThreshold } from '../../elements/coords'
import { resizeListItems } from '../../elements/createElement'
import { collectMoveIds, getGroupChildren, scaleElementsInBounds, syncGroupBounds } from '../../elements/groups'
import { applyListResize } from '../../elements/placementMetrics'
import { boundingRectOf, resolveSelectableId, toggleIdInSelection } from '../../elements/selection'
import { applyResizeSnap, applySnap, buildSnapLines } from '../../elements/snap'
import { scaleTableTracksToBox } from '../../elements/tableListOps'
import { defaultEditorTheme } from '../../templateEditor.theme'
import { Rect, ResizeHandle, TemplateElement } from '../../templateEditor.types'
import { clampRectToCanvas, computeResizeRect } from '../../templateEditor.utils'
import { pointerDelta, ratioForElement, shouldPreserveRatio, startPointerSession } from './documentPointer'
import { useTemplateEditorGuides } from './useTemplateEditorGuides'
import { useTemplateEditorHistory } from './useTemplateEditorHistory'

const theme = defaultEditorTheme

type HistoryApi = ReturnType<typeof useTemplateEditorHistory>
type GuidesApi = ReturnType<typeof useTemplateEditorGuides>

type Params = {
    canvasW: number
    canvasH: number
    showGrid: boolean
    gridSize: number
    elements: TemplateElement[]
    selectedIds: string[]
    setElements: Dispatch<SetStateAction<TemplateElement[]>>
    setSelectedIds: Dispatch<SetStateAction<string[]>>
    history: HistoryApi
    guides: GuidesApi
    zoomRef: MutableRefObject<number>
    patchElement: (id: string, patch: Partial<TemplateElement>) => void
    updateElementRect: (id: string, rect: Rect) => void
}

export const useTemplateEditorTransform = ({
    canvasW,
    canvasH,
    showGrid,
    gridSize,
    elements,
    selectedIds,
    setElements,
    setSelectedIds,
    history,
    guides,
    zoomRef,
    patchElement,
    updateElementRect,
}: Params) => {
    const moveSelectedBy = useCallback(
        (dx: number, dy: number) => {
            const moveIds = collectMoveIds(elements, selectedIds)
            if (moveIds.length === 0) {
                return
            }

            history.pushHistory()
            const idSet = new Set(moveIds)
            setElements((prev) => {
                let next = prev.map((element) =>
                    idSet.has(element.id) && !element.locked
                        ? {
                              ...element,
                              ...clampRectToCanvas(
                                  { x: element.x + dx, y: element.y + dy, w: element.w, h: element.h },
                                  canvasW,
                                  canvasH,
                                  theme.minElSize
                              ),
                          }
                        : element
                )

                for (const id of selectedIds) {
                    const el = next.find((item) => item.id === id)
                    if (el?.type === 'group') {
                        next = syncGroupBounds(next, id)
                    }
                }
                return next
            })
        },
        [canvasH, canvasW, elements, history, selectedIds, setElements]
    )

    const startDragMove = useCallback(
        (id: string, event: ReactMouseEvent) => {
            event.preventDefault()
            event.stopPropagation()

            const resolved = resolveSelectableId(elements, id)
            const additive = event.shiftKey
            let nextSelected: string[]
            if (additive) {
                nextSelected = toggleIdInSelection(selectedIds, resolved)
            } else if (selectedIds.includes(resolved)) {
                nextSelected = selectedIds
            } else {
                nextSelected = [resolved]
            }
            setSelectedIds(nextSelected)

            const target = elements.find((item) => item.id === resolved)
            if (!target || target.locked) {
                return
            }

            const moveIds = collectMoveIds(elements, nextSelected.includes(resolved) ? nextSelected : [resolved])
            if (moveIds.length === 0) {
                return
            }

            const startPositions = new Map(
                elements
                    .filter((element) => moveIds.includes(element.id))
                    .map((element) => [element.id, { ...element }])
            )
            const excludeIds = new Set(moveIds)
            const snapLines = buildSnapLines({
                canvasW,
                canvasH,
                elements,
                showGrid,
                gridSize,
                snapTolerance: theme.snapTolerance,
                excludeIds,
            })
            const startX = event.clientX
            const startY = event.clientY
            let moved = false
            let lastSnapDx = Number.NaN
            let lastSnapDy = Number.NaN

            history.beginPointerInteraction()

            startPointerSession(
                (moveEvent) => {
                    if (
                        !moved &&
                        !dragExceededThreshold(
                            startX,
                            startY,
                            moveEvent.clientX,
                            moveEvent.clientY,
                            theme.dragThreshold
                        )
                    ) {
                        return
                    }
                    moved = true

                    const { dx, dy } = pointerDelta(moveEvent, startX, startY, zoomRef.current)
                    const primaryStart = startPositions.get(resolved)
                    if (!primaryStart) {
                        return
                    }

                    const snapped = applySnap(primaryStart, primaryStart.x + dx, primaryStart.y + dy, {
                        canvasW,
                        canvasH,
                        elements,
                        showGrid,
                        gridSize,
                        snapTolerance: theme.snapTolerance,
                        excludeIds,
                        vLines: snapLines.vLines,
                        hLines: snapLines.hLines,
                    })
                    const snapDx = snapped.x - primaryStart.x
                    const snapDy = snapped.y - primaryStart.y

                    if (snapDx === lastSnapDx && snapDy === lastSnapDy) {
                        guides.setGuides(snapped.verticalGuides, snapped.horizontalGuides)
                        return
                    }

                    lastSnapDx = snapDx
                    lastSnapDy = snapDy

                    setElements((prev) =>
                        prev.map((element) => {
                            const start = startPositions.get(element.id)
                            if (!start || element.locked) {
                                return element
                            }
                            return {
                                ...element,
                                ...clampRectToCanvas(
                                    { x: start.x + snapDx, y: start.y + snapDy, w: start.w, h: start.h },
                                    canvasW,
                                    canvasH,
                                    theme.minElSize
                                ),
                            }
                        })
                    )
                    guides.setGuides(snapped.verticalGuides, snapped.horizontalGuides)
                },
                () => {
                    guides.clearGuides()
                    history.endPointerInteraction()
                },
                { raf: true }
            )
        },
        [
            canvasH,
            canvasW,
            elements,
            gridSize,
            guides,
            history,
            selectedIds,
            setElements,
            setSelectedIds,
            showGrid,
            zoomRef,
        ]
    )

    const startResize = useCallback(
        (id: string, handle: ResizeHandle, event: ReactMouseEvent) => {
            event.preventDefault()
            event.stopPropagation()

            const element = elements.find((item) => item.id === id)
            if (!element || element.locked) {
                return
            }

            setSelectedIds((prev) => (prev.includes(id) ? prev : [id]))

            const startRect: Rect = { x: element.x, y: element.y, w: element.w, h: element.h }
            const startX = event.clientX
            const startY = event.clientY
            const groupChildren =
                element.type === 'group' ? getGroupChildren(elements, element.id).map((child) => cloneDeep(child)) : []
            const scaleIds = element.type === 'group' ? groupChildren.map((child) => child.id) : [element.id]
            const startBounds = element.type === 'group' ? boundingRectOf(groupChildren) ?? startRect : startRect
            const startElementsSnapshot = elements

            const excludeIds = new Set<string>([id, ...scaleIds])
            const snapLines = buildSnapLines({
                canvasW,
                canvasH,
                elements,
                showGrid,
                gridSize,
                snapTolerance: theme.snapTolerance,
                excludeIds,
            })
            let lastRectKey = ''

            history.beginPointerInteraction()

            startPointerSession(
                (moveEvent) => {
                    const { dx, dy } = pointerDelta(moveEvent, startX, startY, zoomRef.current)
                    // Картинка: боковые ручки свободно меняют кадр (поля contain),
                    // углы — равномерный масштаб текущего кадра (не natural, иначе схлопываются поля).
                    const resizeRatio =
                        element.type === 'image' ? startBounds.w / Math.max(startBounds.h, 1) : ratioForElement(element)
                    let nextRect = computeResizeRect(
                        startBounds,
                        handle,
                        dx,
                        dy,
                        canvasW,
                        canvasH,
                        theme.minElSize,
                        shouldPreserveRatio(element) ? { preserveRatio: true, ratio: resizeRatio } : undefined
                    )

                    const snapped = applyResizeSnap(nextRect, handle, {
                        canvasW,
                        canvasH,
                        elements,
                        showGrid,
                        gridSize,
                        snapTolerance: theme.snapTolerance,
                        excludeIds,
                        vLines: snapLines.vLines,
                        hLines: snapLines.hLines,
                        minSize: theme.minElSize,
                    })
                    nextRect = clampRectToCanvas(snapped.rect, canvasW, canvasH, theme.minElSize)
                    guides.setGuides(snapped.verticalGuides, snapped.horizontalGuides)

                    const rectKey = `${nextRect.x}:${nextRect.y}:${nextRect.w}:${nextRect.h}`
                    if (rectKey === lastRectKey) {
                        return
                    }
                    lastRectKey = rectKey

                    if (element.type === 'list') {
                        const synced = applyListResize(element, nextRect, handle, theme, resizeListItems)
                        const clamped = clampRectToCanvas(synced, canvasW, canvasH, theme.minElSize)
                        patchElement(id, { ...clamped, items: synced.items })
                        return
                    }

                    if (element.type === 'table') {
                        const clamped = clampRectToCanvas(nextRect, canvasW, canvasH, theme.minElSize)
                        const scaled = scaleTableTracksToBox(element, clamped.w, clamped.h)
                        patchElement(id, {
                            ...clamped,
                            rowHeight: scaled.rowHeight,
                            colWidth: scaled.colWidth,
                            rowHeights: scaled.rowHeights,
                            colWidths: scaled.colWidths,
                        })
                        return
                    }

                    if (element.type === 'group') {
                        setElements(() => {
                            const scaled = scaleElementsInBounds(startElementsSnapshot, scaleIds, startBounds, nextRect)
                            return syncGroupBounds(scaled, id)
                        })
                        return
                    }

                    updateElementRect(id, nextRect)
                },
                () => {
                    guides.clearGuides()
                    history.endPointerInteraction()
                },
                { raf: true }
            )
        },
        [
            canvasH,
            canvasW,
            elements,
            gridSize,
            guides,
            history,
            patchElement,
            setElements,
            setSelectedIds,
            showGrid,
            updateElementRect,
            zoomRef,
        ]
    )

    return {
        moveSelectedBy,
        startDragMove,
        startResize,
    }
}
